// services/chatStream/chatMessageLimit.ts
//
// How long a chat message may be, and the one sentence the client says about it
// (ORA-750).
//
// The v5 turn route refuses a longer message outright. It used to come back as a
// bare `INVALID_TURN_REQUEST` with nothing in it a student could act on, so a
// pasted lecture looked like the chat was broken; ORA-743 gives it its own code
// and its own copy. This module is the client's half: the cap the composer
// blocks on before anything is sent, and the sentence shown when the server
// refuses a send the composer let through.
//
// It lives here, next to the stream client that reads the refusal, because the
// composer and the transport must agree on the number — two copies of 12,000
// would drift the moment the backend moved it.

/**
 * The longest message the turn route accepts, in characters. Must match
 * `ora-be`'s own limit; the server is the authority and refuses anything longer.
 */
export const CHAT_MESSAGE_MAX_CHARS = 12_000;

/**
 * What the composer says while the draft itself is over the cap.
 *
 * Deliberately static, with no numbers in it: it is rendered in a live region,
 * and a sentence that changed on every keystroke would have a screen reader
 * reading out a new character count per character typed. The numbers live in
 * the counter beside the send control, which is `aria-hidden` for exactly that
 * reason.
 */
export const CHAT_MESSAGE_TOO_LONG_NOTICE =
  'That message is too long. Split it up, or attach it as a document.';

/**
 * What the client says when the SERVER refused a send for its length.
 *
 * The server's own sentence wins when it arrives with `MESSAGE_TOO_LONG`; this
 * is the fallback against a backend that has not shipped the code yet, and it is
 * worded to match that sentence so the student never sees two versions of the
 * same advice. It names the numbers because it is emitted once, in answer to a
 * send that has already happened — not on every keystroke.
 */
export const chatMessageTooLongNotice = (
  length: number,
  limit: number = CHAT_MESSAGE_MAX_CHARS
): string =>
  `That message is ${length.toLocaleString()} characters; the limit is ${limit.toLocaleString()}. Split it up, or attach it as a document.`;

/** The counter beside the send control once a draft is over the cap. */
export const chatMessageLengthCount = (
  length: number,
  limit: number = CHAT_MESSAGE_MAX_CHARS
): string => `${length.toLocaleString()} / ${limit.toLocaleString()}`;
